import { Button, Card, Frame, Page, Spinner, Text } from '@shopify/polaris'
import React from 'react'
import { Navigate } from 'react-router-dom'

import { useAdminAuth } from '../providers/AdminAuthProvider'
import { useFirebase } from '../providers/FirebaseProvider'

interface AdminAuthGuardProps {
  children: React.ReactNode
  requiredPermission?: string
}

export function AdminAuthGuard({
  children,
  requiredPermission,
}: AdminAuthGuardProps) {
  const { user, loading: firebaseLoading } = useFirebase()
  const {
    adminUser,
    loading: adminLoading,
    error,
    hasPermission,
    refetchAdminData,
  } = useAdminAuth()

  if (firebaseLoading || adminLoading) {
    return (
      <Frame>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '32px',
          }}
        >
          <Spinner size="large" />
        </div>
      </Frame>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // If there's an error from the admin API, redirect to login
  // The Firebase user has already been cleared by AdminAuthProvider
  if (error && !adminLoading) {
    return <Navigate to="/login" replace />
  }

  if (!adminUser) {
    return (
      <Frame>
        <Page>
          <Card>
            <div style={{ textAlign: 'center', padding: '32px' }}>
              <Text as="h2" variant="headingMd">
                Unable to verify admin access
              </Text>
              <div style={{ marginTop: '16px', marginBottom: '32px' }}>
                <Text as="p">
                  There was an issue verifying your admin permissions. Please
                  try again.
                </Text>
              </div>
              <Button onClick={refetchAdminData} variant="primary">
                Retry
              </Button>
            </div>
          </Card>
        </Page>
      </Frame>
    )
  }

  if (requiredPermission && !hasPermission(requiredPermission as any)) {
    return (
      <Frame>
        <Page>
          <Card>
            <div style={{ textAlign: 'center', padding: '32px' }}>
              <Text as="h2" variant="headingMd">
                Access Denied
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Text as="p">
                  You don't have permission to access this page. Required
                  permission: {requiredPermission}
                </Text>
              </div>
            </div>
          </Card>
        </Page>
      </Frame>
    )
  }

  return <>{children}</>
}
